import {
  MaturityScheduleRequest,
  MaturityScheduleResponse,
  MaturityScheduleWithTransactionInfo,
  MaturityScheduleFilter,
} from '../../model/MaturitySchedule.model';
import axiosInstance from '../../utils/axiosInstance';

interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

// Interface cho PagedResponse (Spring Data Page)
export interface PagedResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  last: boolean;
  first: boolean;
  numberOfElements: number;
  empty: boolean;
}

export interface MaturityScheduleStats {
  totalSchedules: number;
  pendingCount: number;
  overdueCount: number;
  completedCount: number;
  abandonedCount: number;
}

// Interface cho dữ liệu cập nhật kỳ đáo hạn
export interface MaturityScheduleUpdateRequest {
  repaymentAmount: number;
  feeApplied?: number | null;
  dueDate: string; // YYYY-MM-DD format
  notes?: string;
  isChecked?: boolean;
  isCheckedPayment?: boolean;
}

class MaturityScheduleService {
  private readonly baseUrl = '/maturity-schedule';

  // Tạo một maturity schedule
  async createMaturitySchedule(
    request: MaturityScheduleRequest,
    employeeId: number
  ): Promise<MaturityScheduleResponse> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/create?employeeId=${employeeId}`,
      request
    );
    return response.data;
  }

  // Tạo nhiều maturity schedule cùng lúc
  async createMultiSchedule(
    requests: MaturityScheduleRequest[],
    employeeId: number
  ): Promise<MaturityScheduleResponse[]> {
    const response = await axiosInstance.post(
      `${this.baseUrl}/create-multi?employeeId=${employeeId}`,
      requests
    );
    return response.data;
  }

  async getAllMaturityScheduleByMonth(
    month: number,
    year: number,
    page: number = 0,
    size: number = 20,
    filter?: MaturityScheduleFilter
  ): Promise<PagedResponse<MaturityScheduleWithTransactionInfo>> {
    try {
      const response = await axiosInstance.get(`${this.baseUrl}/get-all-by-month`, {
        params: {
          month,
          year,
          page,
          size,
          ...filter,
        },
      });
      return response.data;
    } catch (error: any) {
      console.error('Error getting maturity schedules by month:', error);

      if (
        error.response?.status === 404 ||
        error.response?.data?.message?.includes('not found')
      ) {
        return {
          content: [],
          totalElements: 0,
          totalPages: 0,
          size: size,
          number: page,
          last: true,
          first: true,
          numberOfElements: 0,
          empty: true,
        };
      }

      throw error;
    }
  }

  async getAllMaturityScheduleByMonthAll(
    month: number,
    year: number
  ): Promise<MaturityScheduleWithTransactionInfo[]> {
    try {
      const response = await axiosInstance.get(
        `${this.baseUrl}/get-all-by-month-all?month=${month}&year=${year}`
      );
      return response.data || [];
    } catch (error: any) {
      console.error('Error getting all maturity schedules by month:', error);
      return [];
    }
  }

  async getAllMaturityScheduleByMonthAndEmployee(
    month: number,
    year: number,
    employeeId: number,
    page: number = 0,
    size: number = 20,
    filter?: MaturityScheduleFilter
  ): Promise<PagedResponse<MaturityScheduleWithTransactionInfo>> {
    try {
      const response = await axiosInstance.get(
        `${this.baseUrl}/get-all-by-month-and-employee`,
        {
          params: {
            month,
            year,
            employeeId,
            page,
            size,
            ...filter,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.error(
        'Error getting maturity schedules by month and employee:',
        error
      );

      if (
        error.response?.status === 404 ||
        error.response?.data?.message?.includes('not found')
      ) {
        return {
          content: [],
          totalElements: 0,
          totalPages: 0,
          size: size,
          number: page,
          last: true,
          first: true,
          numberOfElements: 0,
          empty: true,
        };
      }

      throw error;
    }
  }

  async getAllMaturityScheduleByMonthAndEmployeeAll(
    month: number,
    year: number,
    employeeId: number
  ): Promise<MaturityScheduleWithTransactionInfo[]> {
    try {
      const response = await axiosInstance.get(
        `${this.baseUrl}/get-all-by-month-and-employee-all?month=${month}&year=${year}&employeeId=${employeeId}`
      );
      return response.data || [];
    } catch (error: any) {
      console.error(
        'Error getting all maturity schedules by month and employee:',
        error
      );
      return [];
    }
  }

  // Lấy tất cả maturity schedule theo tháng, năm và thẻ tín dụng
  async getAllMaturityScheduleByMonthAndCard(
    month: number,
    year: number,
    cardId: number
  ): Promise<MaturityScheduleWithTransactionInfo[]> {
    try {
      const response = await axiosInstance.get(
        `${this.baseUrl}/get-all-by-month-and-card?month=${month}&year=${year}&cardId=${cardId}`
      );
      return response.data || [];
    } catch (error: any) {
      console.error(
        'Error getting maturity schedules by month and card:',
        error
      );

      // Handle 404 or no data scenarios
      if (
        error.response?.status === 404 ||
        error.response?.data?.message?.includes('not found')
      ) {
        return [];
      }

      throw error;
    }
  }

  // Lấy tất cả maturity schedule theo năm và thẻ tín dụng (cả năm)
  async getAllMaturityScheduleByYearAndCard(
    year: number,
    cardId: number
  ): Promise<MaturityScheduleWithTransactionInfo[]> {
    try {
      const response = await axiosInstance.get(
        `${this.baseUrl}/get-all-by-year-and-card?year=${year}&cardId=${cardId}`
      );
      return response.data || [];
    } catch (error: any) {
      console.error(
        'Error getting maturity schedules by year and card:',
        error
      );

      // Handle 404 or no data scenarios
      if (
        error.response?.status === 404 ||
        error.response?.data?.message?.includes('not found')
      ) {
        return [];
      }

      throw error;
    }
  }

  async getAllMaturityScheduleByMonthWithTransactions(
    month: number,
    year: number,
    page: number = 0,
    size: number = 20
  ): Promise<PagedResponse<MaturityScheduleWithTransactionInfo>> {
    try {
      const response = await axiosInstance.get(
        `${this.baseUrl}/get-all-by-month-with-transactions?month=${month}&year=${year}&page=${page}&size=${size}`
      );
      return response.data;
    } catch (error: any) {
      console.error(
        'Error getting maturity schedules by month with transactions:',
        error
      );

      if (
        error.response?.status === 404 ||
        error.response?.data?.message?.includes('not found')
      ) {
        return {
          content: [],
          totalElements: 0,
          totalPages: 0,
          size: size,
          number: page,
          last: true,
          first: true,
          numberOfElements: 0,
          empty: true,
        };
      }

      throw error;
    }
  }

  // Lấy tất cả maturity schedule theo tháng, năm và thẻ với thông tin giao dịch chi tiết
  async getAllMaturityScheduleByMonthAndCardWithTransactions(
    month: number,
    year: number,
    cardId: number
  ): Promise<MaturityScheduleWithTransactionInfo[]> {
    try {
      const response = await axiosInstance.get(
        `${this.baseUrl}/get-all-by-month-and-card-with-transactions?month=${month}&year=${year}&cardId=${cardId}`
      );
      return response.data || [];
    } catch (error: any) {
      console.error(
        'Error getting maturity schedules by month and card with transactions:',
        error
      );

      // Handle 404 or no data scenarios
      if (
        error.response?.status === 404 ||
        error.response?.data?.message?.includes('not found')
      ) {
        return [];
      }

      throw error;
    }
  }

  // Cập nhật kỳ đáo hạn
  async updateMaturitySchedule(
    id: number,
    updateData: MaturityScheduleUpdateRequest,
    employeeId: number
  ): Promise<ApiResponse<MaturityScheduleWithTransactionInfo>> {
    const response = await axiosInstance.put(
      `${this.baseUrl}/${id}?employeeId=${employeeId}`,
      updateData
    );
    return response.data;
  }

  // Xóa kỳ đáo hạn
  async deleteMaturitySchedule(
    id: number,
    employeeId: number
  ): Promise<ApiResponse<string>> {
    const response = await axiosInstance.delete(
      `${this.baseUrl}/${id}?employeeId=${employeeId}`
    );
    return response.data;
  }

  // Lấy chi tiết kỳ đáo hạn
  async getMaturityScheduleDetail(
    id: number
  ): Promise<ApiResponse<MaturityScheduleWithTransactionInfo>> {
    const response = await axiosInstance.get(`${this.baseUrl}/${id}`);
    return response.data;
  }

  // Cập nhật nhanh ghi chú kỳ đáo hạn
  async updateMaturityScheduleNotes(
    id: number,
    notes: string,
    employeeId: number
  ): Promise<ApiResponse<string>> {
    const response = await axiosInstance.patch(
      `${this.baseUrl}/${id}/notes?notes=${encodeURIComponent(notes)}&employeeId=${employeeId}`
    );
    return response.data;
  }

  // Cập nhật nhanh trạng thái kỳ đáo hạn
  async updateMaturityScheduleStatus(
    id: number,
    status: string,
    employeeId: number
  ): Promise<ApiResponse<string>> {
    const response = await axiosInstance.patch(
      `${this.baseUrl}/${id}/status?status=${status}&employeeId=${employeeId}`
    );
    return response.data;
  }

  // Toggle trạng thái hoàn thành kỳ đáo hạn
  async toggleCompletionStatus(
    id: number,
    employeeId: number
  ): Promise<ApiResponse<string>> {
    const response = await axiosInstance.patch(
      `${this.baseUrl}/${id}/toggle-completion?employeeId=${employeeId}`
    );
    return response.data;
  }

  // Lấy danh sách kỳ đáo hạn được tạo trong ngày (theo createdAt)
  async getCreatedToday(): Promise<{ data: MaturityScheduleResponse[] }> {
    const response = await axiosInstance.get(`${this.baseUrl}/created-today`);
    return response.data;
  }

  // Lấy tất cả kỳ đáo hạn đến hạn hoặc quá hạn hôm nay
  async getDueOrOverdueToday(): Promise<MaturityScheduleWithTransactionInfo[]> {
    const response = await axiosInstance.get(
      '/maturity-schedule/due-or-overdue-today'
    );
    return response.data || [];
  }

  async getWrongPaidSchedulesByMonthAndYear(
    month: number,
    year: number
  ): Promise<MaturityScheduleWithTransactionInfo[]> {
    const response = await axiosInstance.get(
      `/maturity-schedule/wrong-paid-by-month?month=${month}&year=${year}`
    );
    return response.data || [];
  }

  async getWrongPaymentSchedulesByMonthAndYear(
    month: number,
    year: number
  ): Promise<MaturityScheduleWithTransactionInfo[]> {
    const response = await axiosInstance.get(
      `/maturity-schedule/wrong-payment-by-month?month=${month}&year=${year}`
    );
    return response.data || [];
  }

  async checkMaturitySchedule(id: number) {
    return axiosInstance.patch(`/maturity-schedule/${id}/check`);
  }

  async checkPaymentMaturitySchedule(id: number) {
    return axiosInstance.patch(`/maturity-schedule/${id}/check-payment`);
  }

  // Lấy thống kê kỳ đáo hạn theo tháng
  async getMaturityScheduleStats(
    month: number,
    year: number,
    employeeId?: number
  ): Promise<MaturityScheduleStats> {
    const url = employeeId
      ? `${this.baseUrl}/stats?month=${month}&year=${year}&employeeId=${employeeId}`
      : `${this.baseUrl}/stats?month=${month}&year=${year}`;
    const response = await axiosInstance.get(url);
    return response.data;
  }
}

export default new MaturityScheduleService();
